import { useSyncExternalStore } from 'react'
import arabic from '@/locales/ar.json'

export type Language = 'en' | 'ar'
export const LANGUAGE_STORAGE_KEY = 'ielts.interface-language'
const listeners = new Set<() => void>()

function savedLanguage(): Language {
  try { return localStorage.getItem(LANGUAGE_STORAGE_KEY) === 'ar' ? 'ar' : 'en' } catch { return 'en' }
}
let language: Language = savedLanguage()

export function setLanguage(next: Language) {
  if (next !== 'en' && next !== 'ar') return
  language = next
  try { localStorage.setItem(LANGUAGE_STORAGE_KEY, next) } catch { /* The current tab still works with storage disabled. */ }
  listeners.forEach((notify) => notify())
}

function subscribe(notify: () => void) {
  listeners.add(notify)
  return () => { listeners.delete(notify) }
}

/** Subscribe without remounting the page or resetting an in-progress form. */
export function useLanguage() {
  return useSyncExternalStore(subscribe, () => language, () => 'en' as const)
}

/** Only source-owned interface copy belongs here, never exam or user content. */
export function t(source: string, values: Record<string, unknown> = {}): string {
  const translated = language === 'ar' ? (arabic as Record<string, string>)[source.trim()] : undefined
  const message = translated ? source.replace(source.trim(), () => translated) : source
  return message.replace(/\{(\d+)\}/g, (placeholder, key: string) =>
    Object.hasOwn(values, key) ? String(values[key]) : placeholder)
}

/** Existing count labels use count-neutral Arabic copy, with no English suffix. */
export function englishPluralSuffix(count: number | null | undefined) {
  return language === 'en' && count !== 1 ? 's' : ''
}

export function dateLocale() {
  // Keep the existing Gregorian dates and Latin score digits in both languages.
  return language === 'ar' ? 'ar-u-ca-gregory-nu-latn' : 'en-GB'
}

export function isEnglishTestRoute(pathname: string) {
  return /^\/test\/[^/]+\/?$/i.test(pathname) || /^\/admin\/tests\/[^/]+\/preview\/?$/i.test(pathname)
}
